<template>
  <v-card outlined class="modern-planner-card elevation-0 overflow-hidden" :loading="loading">
    <v-toolbar flat :color="dragEnable ? 'primary' : 'secondary lighten-4'" dark height="64">
      <v-toolbar-title class="font-weight-black body-1">
        {{ dragEnable ? 'Secuencia' : 'Tareas' }}
        <span class="font-weight-medium pl-1">· {{ machine.name }}</span>
      </v-toolbar-title>
      <v-spacer />
      <div v-if="!hideChrome" class="d-none d-md-flex align-center mr-4">
        <v-chip v-if="machine && machine.capacity" dark label small class="mr-2" color="rgba(255,255,255,0.12)">
          Cap. {{ parseInt(machine.capacity, 10) }} {{ machine.production_unit_m2 ? 'm²' : 'un' }}/h
        </v-chip>
        <v-chip v-if="machine && machine.hours_per_day" dark label small color="rgba(255,255,255,0.12)">
          {{ machine.hours_per_day }}h / día
        </v-chip>
      </div>
      <export-machine-planner :machine="machine" :secuence="secuence" :disabled="loading" />
      <v-btn v-if="!hideChrome" icon color="white" class="ml-1" @click="getMachineData" :disabled="loading" title="Recargar">
        <v-icon small>fas fa-sync-alt</v-icon>
      </v-btn>
      <v-btn
        v-if="dragEnable && !hideChrome"
        icon
        color="white"
        class="ml-1"
        @click="savePlanner"
        :disabled="loading"
        :loading="saving"
        title="Guardar cambios"
      >
        <v-icon small>fas fa-save</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="px-4 py-3 grey lighten-5 d-flex align-center flex-wrap" style="gap: 8px;">
      <v-chip label outlined color="primary" small class="font-weight-bold">
        {{ (machine.production_unit_m2 ? totalOnQueue.to_complete_m2 : totalOnQueue.to_complete_qty) | number('0,0') }}
        {{ machine.production_unit_m2 ? 'm²' : 'un' }} pendiente
      </v-chip>
      <v-chip label outlined color="secondary" small class="font-weight-bold">
        {{ estimatedHours }} h
      </v-chip>
      <v-chip label outlined color="warning" small class="font-weight-bold">
        {{ estimatedDays }} días
      </v-chip>
      <v-spacer />
      <v-select
        v-if="!hideChrome && !dragEnable && filterEquipment == null"
        v-model="localEquipment"
        :items="equipmentList"
        label="Equipo"
        outlined dense hide-details
        style="max-width: 180px;"
        :disabled="loading"
      />
      <v-text-field
        v-if="!hideChrome"
        v-model="localSearch"
        placeholder="Buscar OT, cliente o producto"
        prepend-inner-icon="fas fa-search"
        outlined dense hide-details
        style="max-width: 260px;"
        :disabled="loading"
      />
      <v-btn color="primary" depressed small @click="addRegister" :disabled="loading" class="font-weight-bold">
        Reportar
      </v-btn>
      <v-btn v-if="dragEnable" color="grey darken-3" dark depressed small @click="asignEquipment" :disabled="loading" class="font-weight-bold">
        Asignar
      </v-btn>
    </div>

    <v-card-text class="pa-0">
      <div v-if="loading" class="pa-6">
        <v-skeleton-loader type="table-heading, table-row@5" />
      </div>
      <v-data-iterator
        v-show="!loading"
        :items="listItems"
        item-key="process"
        :items-per-page="-1"
        hide-default-footer
        @current-items="detect"
      >
        <template v-slot:default="{ items }">
          <v-simple-table class="modern-table planner-table">
            <thead>
              <tr>
                <th width="50" class="text-center">
                  <v-checkbox v-model="allSelected" @change="toggleSelectAll" hide-details dense class="ma-0 pa-0 d-inline-block" />
                </th>
                <th width="72" class="text-center">Pos.</th>
                <th width="88" class="text-center">OT</th>
                <th width="180" class="text-left">Cliente</th>
                <th class="text-left">Producto y proceso</th>
                <th width="180" class="text-left">Máquina</th>
                <th width="140" class="text-center">Avance</th>
                <th width="110" class="text-right">Comprometida</th>
                <th width="110" class="text-right">Estimada</th>
              </tr>
            </thead>
            <draggable
              :disabled="!canReorder"
              v-model="secuence"
              @change="secuenceChange"
              draggable=".draggable-row"
              tag="tbody"
              ghost-class="ghost-row"
            >
              <tr
                v-for="item in items"
                :key="item.process || item.id"
                :class="{
                  'draggable-row': canReorder,
                  'overdue-row': item.risk === 'late',
                  'risk-row': item.risk === 'risk',
                  'completed-row-dim': item.done,
                  'is-match': resolvedSearch && isRowMatch(item)
                }"
              >
                <td class="text-center">
                  <v-checkbox v-model="item.completed" hide-details dense class="ma-0 pa-0 d-inline-block" color="primary" />
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center">
                    <span class="caption font-weight-black">{{ secuence.indexOf(item) + 1 }}</span>
                    <v-icon v-if="item.reprocess" color="warning" x-small class="ml-2">fas fa-redo</v-icon>
                  </div>
                </td>
                <td class="text-center">
                  <show-work-order :work_order_id="item.work_order" class="caption font-weight-black primary--text">{{ item.work_order }}</show-work-order>
                </td>
                <td class="text-left">
                  <div class="caption font-weight-bold text-truncate" style="max-width: 170px;">{{ item.customer_name }}</div>
                </td>
                <td class="py-3 text-left">
                  <div v-if="item.product_sku" class="caption font-weight-bold secondary--text">[{{ item.product_sku }}]</div>
                  <div class="body-2 font-weight-medium">{{ item.product_name }}</div>
                  <div class="caption font-weight-bold primary--text">
                    {{ item.name }}
                    <span v-if="item.equipment_assigned" class="secondary--text font-weight-medium"> · {{ item.equipment_assigned }}</span>
                  </div>
                </td>
                <td class="text-left" @mousedown.stop>
                  <v-select
                    v-if="dragEnable"
                    :value="equipmentIdOf(item)"
                    :items="equipmentSelectItems"
                    dense outlined hide-details
                    placeholder="Sin asignar"
                    class="mt-0 pt-0"
                    @change="onAssignMachine(item, $event)"
                  />
                  <span v-else class="caption font-weight-bold">{{ item.equipment_assigned || 'Sin asignar' }}</span>
                </td>
                <td>
                  <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="pt-1">
                        <div class="d-flex justify-space-between caption font-weight-bold mb-1">
                          <span v-if="machine.production_unit_m2">{{ item.total_mts2 | number('0.00') }} / {{ (item.product_mts2 * (item.qty_pp || 1)) | number('0.00') }}</span>
                          <span v-else>{{ item.total_qty | number('0.0') }} / {{ (item.product_qty * (item.qty_pp || 1) + (item.merma || 0)) | number('0.0') }}</span>
                        </div>
                        <v-progress-linear
                          :color="item.done ? 'success' : 'primary'"
                          height="6"
                          rounded
                          :value="progressOf(item)"
                        />
                      </div>
                    </template>
                    <bitacora-details-planner :process="item" />
                  </v-tooltip>
                </td>
                <td class="text-right caption font-weight-bold">
                  {{ item.delivery_date | moment('D/MM/YYYY') }}
                </td>
                <td class="text-right caption font-weight-bold">
                  <span :class="item.risk === 'late' ? 'error--text' : item.risk === 'risk' ? 'warning--text' : 'success--text'">
                    {{ item.end_date | moment('D/MM/YYYY') }}
                  </span>
                </td>
              </tr>
            </draggable>
          </v-simple-table>
        </template>
      </v-data-iterator>
    </v-card-text>

    <v-dialog v-model="showCompleted" :max-width="machine.hour_control ? 1200 : 1100" persistent>
      <v-card outlined>
        <process-complete
          :processes="completed"
          :machine="machine"
          @refresh="getMachineData"
          @close="showCompleted = false"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAsignEquipment" max-width="600" persistent>
      <v-card outlined>
        <asign-equipment
          :processes="toAsignEquipment"
          :machine="machine"
          @refresh="getMachineData"
          @close="showAsignEquipment = false"
        />
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable'
import ProcessComplete from '@/components/production/ProcessComplete'
import BitacoraDetailsPlanner from '@/components/production/BitacoraDetailsPlanner'
import ExportMachinePlanner from '@/components/production/ExportMachinePlanner'
import { updatePlanner } from '@/api/production'
import { fetchPlannerByMachine } from '@/api/reports'
import notifications from '../../utils/notifications'
import AsignEquipment from '@/components/production/AsignEquipment'
import {
  estimateMachineQueue,
  matchesSearch,
  matchesEquipment,
  remainingWork,
  buildSavePayload,
} from '@/utils/plannerEstimate'

export default {
  name: 'MachinePlannerAlone',
  components: { draggable, ProcessComplete, BitacoraDetailsPlanner, ExportMachinePlanner, AsignEquipment },
  props: {
    machine_id: [Number, String],
    machineData: { type: Object, default: null },
    dragEnable: { type: Boolean, default: false },
    hideChrome: { type: Boolean, default: false },
    search: { type: String, default: null },
    filterEquipment: { type: String, default: null },
    riskFilter: { type: String, default: 'all' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      secuence: [],
      currentItems: [],
      localSearch: '',
      localEquipment: 'Todos',
      machine: { name: '', equipments: [] },
      allSelected: false,
      showCompleted: false,
      showAsignEquipment: false,
      toAsignEquipment: [],
      completed: [],
    }
  },
  computed: {
    resolvedSearch() {
      return this.search != null ? this.search : this.localSearch
    },
    resolvedEquipment() {
      return this.filterEquipment != null ? this.filterEquipment : this.localEquipment
    },
    equipmentList() {
      return this.machine && this.machine.equipments
        ? ['Todos', ...this.machine.equipments.map((i) => i.name)]
        : ['Todos']
    },
    equipmentSelectItems() {
      const items = [{ text: 'Sin asignar', value: null }]
      ;(this.machine.equipments || []).filter((eq) => eq.enabled !== false).forEach((eq) => {
        items.push({ text: eq.name, value: eq.id })
      })
      return items
    },
    visibleSequence() {
      return this.secuence.filter((item) => {
        if (!matchesEquipment(item, this.resolvedEquipment)) return false
        if (this.riskFilter === 'late' && item.risk !== 'late') return false
        if (this.riskFilter === 'risk' && item.risk !== 'risk') return false
        return matchesSearch(item, this.resolvedSearch)
      })
    },
    isQueueFiltered() {
      const equipment = this.resolvedEquipment
      const search = (this.resolvedSearch || '').trim()
      return (equipment && equipment !== 'Todos') || !!search || (this.riskFilter && this.riskFilter !== 'all')
    },
    canReorder() {
      return this.dragEnable && !this.isQueueFiltered
    },
    listItems() {
      return this.canReorder ? this.secuence : this.visibleSequence
    },
    totalOnQueue() {
      const source = this.visibleSequence
      const m2 = source.reduce((t, i) => t + remainingWork(i, true), 0)
      const qty = source.reduce((t, i) => t + remainingWork(i, false), 0)
      return { to_complete_m2: m2, to_complete_qty: qty }
    },
    estimatedHours() {
      return Math.ceil(this.visibleSequence.reduce((t, i) => t + (i.estimated_hours || 0), 0))
    },
    estimatedDays() {
      const hours = this.machine.hours_per_day || 1
      return hours ? Math.ceil(this.estimatedHours / hours) : 0
    },
    queueSignature() {
      if (!this.machineData || !this.machineData.queue) return ''
      return this.machineData.queue.map((p) => p.process + ':' + (p.end_date || '')).join('|')
    },
  },
  methods: {
    detect(value) { this.currentItems = value },
    isRowMatch(item) {
      return matchesSearch(item, this.resolvedSearch)
    },
    equipmentIdOf(item) {
      const found = (this.machine.equipments || []).find((eq) => eq.name === item.equipment_assigned)
      return found ? found.id : null
    },
    onAssignMachine(item, equipmentId) {
      const equipment = (this.machine.equipments || []).find((eq) => eq.id === equipmentId) || null
      this.$emit('assign-equipment', {
        process: item,
        machineId: this.machine.id,
        equipmentId: equipment ? equipment.id : null,
        equipmentName: equipment ? equipment.name : '',
      })
    },
    progressOf(item) {
      const qtyPp = item.qty_pp || 1
      if (this.machine.production_unit_m2) {
        const target = (item.product_mts2 || 0) * qtyPp
        return target ? (item.total_mts2 / target) * 100 : 0
      }
      const target = (item.product_qty || 0) * qtyPp + (item.merma || 0)
      return target ? (item.total_qty / target) * 100 : 0
    },
    applyMachine(machine) {
      this.machine = machine
      this.secuence = Array.isArray(machine.queue)
        ? machine.queue
        : estimateMachineQueue(machine)
    },
    async savePlanner() {
      this.saving = true
      try {
        this.machine.queue = this.secuence
        await updatePlanner(buildSavePayload([this.machine]))
        notifications.showSuccess(`Planificación de ${this.machine.name} actualizada`)
        await this.getMachineData()
      } catch (e) {
        notifications.showError('Error al guardar: ' + e)
      } finally {
        this.saving = false
      }
    },
    secuenceChange() {
      this.machine.queue = this.secuence
      estimateMachineQueue(this.machine)
      this.$emit('queue-change', this.secuence)
    },
    toggleSelectAll() {
      this.currentItems.forEach((s) => { s.completed = this.allSelected })
    },
    addRegister() {
      this.completed = this.secuence.filter((proc) => proc.completed)
      if (this.completed.length === 0) {
        notifications.showInfo('Seleccione al menos un proceso para reportar')
        return
      }
      this.completed.forEach((proc) => {
        proc.real_mts2 = 0
        proc.real_qty = 0
        proc.ask_completed = false
      })
      this.showCompleted = true
    },
    asignEquipment() {
      this.toAsignEquipment = this.secuence.filter((proc) => proc.completed)
      if (this.toAsignEquipment.length === 0) {
        notifications.showInfo('Seleccione uno o varios procesos para asignar equipo')
        return
      }
      this.showAsignEquipment = true
    },
    async getMachineData() {
      if (this.machineData) {
        this.$emit('refresh')
        return
      }
      if (!this.machine_id) return
      this.loading = true
      try {
        const res = await fetchPlannerByMachine(`machine=${this.machine_id}`)
        this.applyMachine(res)
      } catch (err) {
        notifications.showError('Error al cargar datos del área: ' + err)
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    if (this.machineData) this.applyMachine(this.machineData)
    else if (this.machine_id) this.getMachineData()
  },
  watch: {
    machine_id() {
      if (!this.machineData) this.getMachineData()
    },
    queueSignature() {
      if (this.machineData) this.applyMachine(this.machineData)
    },
  },
}
</script>

<style scoped>
.modern-planner-card { border: 1px solid rgba(26, 31, 46, 0.08) !important; border-radius: 12px !important; }
.planner-table >>> th { padding: 10px 12px !important; font-size: 0.72rem !important; letter-spacing: 0.04em; text-transform: uppercase; color: #64748b; }
.planner-table >>> td { padding: 8px 12px !important; border-bottom: 1px solid rgba(26, 31, 46, 0.05) !important; }
.overdue-row { background-color: rgba(185, 28, 28, 0.06); }
.risk-row { background-color: rgba(180, 83, 9, 0.06); }
.completed-row-dim { opacity: 0.5; }
.is-match { box-shadow: inset 3px 0 0 #35bdc8; }
.draggable-row { cursor: grab; }
.draggable-row:active { cursor: grabbing; }
.ghost-row { opacity: 0.4; background: #e0f7f9; }
</style>
